/**
 * A register row opens its record on the record's own page, never in a panel
 * beside the list (split panes are banned, see CLAUDE.md). These two
 * hooks carry the list's URL to that page and back, so the date range, status
 * chip, assignee and work type the reader had set are still set when they
 * return.
 *
 * The list's filters therefore have to live in its URL. A filter held only in
 * component state is lost the moment the list unmounts, whatever the back
 * arrow does.
 */
import { useLocation, useNavigate } from 'react-router-dom';

interface RecordPageState {
  listUrl?: unknown;
}

/** From a list: open `to`, remembering this list's path and query. */
export function useOpenRecord(): (to: string) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return (to: string) => navigate(to, { state: { listUrl: `${location.pathname}${location.search}` } });
}

/**
 * On a record's page: the back arrow. Opened from `listPath`, it returns to
 * that list exactly as it was left, filters and scroll position both. Opened from anywhere else in the app (a
 * component's run history, an invoice line), it goes back there. Opened cold
 * (a pasted link, a new tab), it goes to the unfiltered list.
 */
export function useBackToList(listPath: string): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  const listUrl = (location.state as RecordPageState | null)?.listUrl;
  return () => {
    if (typeof listUrl === 'string' && listUrl.startsWith(listPath)) {
      // `listUrl` only arrives by a navigation FROM that list (useOpenRecord,
      // or a record page replacing itself with the next record), so the entry
      // behind this one is the list as it was left. Step back to it rather
      // than pushing its URL again: a POP is what useScrollRestoration answers,
      // so the reader lands on the row they opened, not at the top.
      navigate(-1);
      return;
    }
    // react-router's BrowserRouter stamps `idx` on history.state; 0 means this
    // page is the first entry, so there is nothing in the app to go back to.
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) navigate(-1);
    else navigate(listPath);
  };
}
